import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";

const LangSwitcher = ({ mobile = false }) => {
    const { lang, setLang } = useLanguage();
    return (
        <div className={`flex items-center gap-1 ${mobile ? "" : "mr-2"}`} data-testid="language-switcher">
            {["it", "en"].map((l) => (
                <button
                    key={l}
                    onClick={() => setLang(l)}
                    data-testid={`lang-${l}`}
                    className={`px-2 py-1 text-[11px] tracking-[0.2em] font-semibold uppercase transition-colors duration-200 ${
                        lang === l ? "text-black border-b-2 border-black" : "text-zinc-400 hover:text-black"
                    }`}
                >
                    {l}
                </button>
            ))}
        </div>
    );
};

const NAV_LINKS = [
    { to: "/", label: "HOME" },
    { to: "/shop", label: "SHOP" },
    { to: "/about", label: "ABOUT" },
    { to: "/lookbook", label: "LOOKBOOK" },
];

export default function Navbar() {
    const { count, setIsOpen } = useCart();
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-zinc-200"
            data-testid="navbar"
        >
            <nav className="relative flex items-center justify-between px-5 md:px-10 h-16 md:h-20">
                {/* Left links (desktop) */}
                <div className="hidden md:flex items-center gap-8 flex-1">
                    {NAV_LINKS.slice(0, 2).map((link) => (
                        <Link
                            key={link.to}
                            to={link.to}
                            data-testid={`nav-${link.label.toLowerCase()}`}
                            className={`text-xs tracking-[0.25em] font-semibold transition-colors duration-300 hover:text-zinc-400 ${
                                location.pathname === link.to ? "text-black" : "text-zinc-500"
                            }`}
                        >
                            {link.label}
                        </Link>
                    ))}
                </div>

                {/* Mobile menu button */}
                <button
                    className="md:hidden p-2 -ml-2"
                    onClick={() => setMobileOpen(!mobileOpen)}
                    aria-label="Menu"
                    data-testid="mobile-menu-button"
                >
                    {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                </button>

                {/* Centered wordmark */}
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="absolute left-1/2 -translate-x-1/2 font-display text-2xl md:text-3xl font-extrabold tracking-[0.35em] uppercase"
                >
                    SEVA
                </Link>

                {/* Right links + cart */}
                <div className="hidden md:flex items-center gap-8 flex-1 justify-end">
                    {NAV_LINKS.slice(2).map((link) => (
                        <Link
                            key={link.to}
                            to={link.to}
                            data-testid={`nav-${link.label.toLowerCase()}`}
                            className={`text-xs tracking-[0.25em] font-semibold transition-colors duration-300 hover:text-zinc-400 ${
                                location.pathname === link.to ? "text-black" : "text-zinc-500"
                            }`}
                        >
                            {link.label}
                        </Link>
                    ))}
                    <LangSwitcher />
                    <button
                        onClick={() => setIsOpen(true)}
                        className="relative p-2 transition-transform duration-300 hover:scale-110"
                        aria-label="Carrello"
                        data-testid="cart-button"
                    >
                        <ShoppingBag size={20} strokeWidth={1.8} />
                        {count > 0 && (
                            <span
                                data-testid="cart-count-badge"
                                className="absolute -top-1 -right-1 bg-black text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
                            >
                                {count}
                            </span>
                        )}
                    </button>
                </div>

                {/* Mobile cart */}
                <button
                    onClick={() => setIsOpen(true)}
                    className="md:hidden relative p-2 -mr-2"
                    aria-label="Carrello"
                    data-testid="cart-button-mobile"
                >
                    <ShoppingBag size={20} strokeWidth={1.8} />
                    {count > 0 && (
                        <span className="absolute -top-1 -right-1 bg-black text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                            {count}
                        </span>
                    )}
                </button>
            </nav>

            {/* Mobile menu */}
            {mobileOpen && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="md:hidden bg-white border-t border-zinc-200"
                    data-testid="mobile-menu"
                >
                    <div className="flex flex-col px-5 py-4 gap-4">
                        {NAV_LINKS.map((link) => (
                            <Link
                                key={link.to}
                                to={link.to}
                                onClick={() => setMobileOpen(false)}
                                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                                className="text-sm tracking-[0.25em] font-semibold uppercase"
                            >
                                {link.label}
                            </Link>
                        ))}
                        <div className="pt-2 border-t border-zinc-100">
                            <LangSwitcher mobile />
                        </div>
                    </div>
                </motion.div>
            )}
        </motion.header>
    );
}
